import { Dialog } from "@foldkit/ui";
import type { Html, HtmlBuilder } from "foldkit/html";

import { Trash2 } from "./icons";

export { Dialog };

export type ConfirmDialogConfig<ParentMessage> = Readonly<{
	slotId: string;
	model: Dialog.Model;
	toParentMessage: (message: Dialog.Message) => ParentMessage;
	title: string;
	message: string;
	/** Optional detail lines under the message, one `<li>` each. */
	lines?: ReadonlyArray<string>;
	confirmLabel: string;
	cancelLabel: string;
	onConfirm: ParentMessage;
	isPending: boolean;
}>;

/**
 * v1's `ConfirmDeleteDialog` on the headless `Dialog`: a native `<dialog>`
 * (Escape closes it through `OnCancel`), a DaisyUI `modal-box`, Cancel wired
 * to the dialog's own close, Confirm to the parent's Message. Both buttons
 * disable while the parent's mutation is pending.
 */
export const confirmDialog = <ParentMessage>(
	h: HtmlBuilder<ParentMessage>,
	config: ConfirmDialogConfig<ParentMessage>,
): Html =>
	h.submodel({
		slotId: config.slotId,
		view: Dialog.view,
		model: config.model,
		toParentMessage: config.toParentMessage,
		viewInputs: {
			toView: (render) =>
				h.dialog(
					[...render.dialog, h.Class("modal")],
					render.isVisible
						? [
								h.div([...render.backdrop, h.Class("modal-backdrop")], []),
								h.div(
									[...render.panel, h.Class("modal-box")],
									[
										h.div(
											[h.Class("flex items-center gap-2 mb-4")],
											[
												Trash2(h, { class: "text-error" }),
												h.h3(
													[...render.title, h.Class("font-bold text-lg")],
													[config.title],
												),
											],
										),
										h.p([...render.description], [config.message]),
										config.lines === undefined || config.lines.length === 0
											? h.empty
											: h.ul(
													[h.Class("mt-3 list-disc pl-5 text-sm")],
													config.lines.map((line) => h.li([], [line])),
												),
										h.div(
											[h.Class("modal-action")],
											[
												h.button(
													[
														...render.closeButton,
														h.Class("btn"),
														h.Disabled(config.isPending),
														h.AriaLabel(config.cancelLabel),
													],
													[config.cancelLabel],
												),
												h.button(
													[
														h.Type("button"),
														h.Class("btn btn-error"),
														h.Disabled(config.isPending),
														h.AriaBusy(config.isPending),
														h.AriaLabel(config.confirmLabel),
														h.OnClick(config.onConfirm),
													],
													[
														...(config.isPending
															? [
																	h.span(
																		[
																			h.Class(
																				"loading loading-spinner loading-sm",
																			),
																		],
																		[],
																	),
																]
															: []),
														config.confirmLabel,
													],
												),
											],
										),
									],
								),
							]
						: [],
				),
		},
	});
